import { Delete as DeleteIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from 'components/ui/combobox';
import { FieldLabel } from 'components/ui/field';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import React, { useId, useState } from 'react';
import { t } from 'utils/getTranslations';

interface ListPickerOption {
    label: string;
    value: string;
}

interface ListPickerInputProps {
    options: ListPickerOption[];
    onListChange: (items: string[]) => void;
    label?: string;
    placeholder?: string;
}

const ListPickerInput: React.FC<ListPickerInputProps> = ({
    options,
    onListChange,
    label = 'select-items',
    placeholder = 'search-select',
}) => {
    const inputId = useId();
    const [selectedItems, setSelectedItems] = useState<ListPickerOption[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');

    const handleSelect = (selectedOption: ListPickerOption | null) => {
        if (selectedOption && !selectedItems.some((item) => item.value === selectedOption.value)) {
            const updatedItems = [...selectedItems, selectedOption];
            setSelectedItems(updatedItems);
            onListChange(updatedItems.map((item) => item.value));
            setSearchValue('');
        }
    };

    const handleDelete = (valueToDelete: string) => {
        const updatedItems = selectedItems.filter((item) => item.value !== valueToDelete);
        setSelectedItems(updatedItems);
        onListChange(updatedItems.map((item) => item.value));
    };

    const availableOptions = options.filter(
        (option) => !selectedItems.some((selected) => selected.value === option.value)
    );

    return (
        <div>
            <FieldLabel className="text-fg-secondary mb-1 font-normal" htmlFor={inputId}>
                {t(label)}
            </FieldLabel>
            <Combobox<ListPickerOption>
                items={availableOptions}
                value={null}
                inputValue={searchValue}
                onInputValueChange={(next) => setSearchValue(next)}
                onValueChange={(selectedOption) => handleSelect(selectedOption)}
                itemToStringLabel={(option) => option.label}
                isItemEqualToValue={(option, value) => option.value === value.value}
            >
                <ComboboxInput id={inputId} placeholder={t(placeholder)} />
                <ComboboxContent className="shadow-dropdown">
                    <ComboboxEmpty>{t('no-options')}</ComboboxEmpty>
                    <ComboboxList>
                        {(option: ListPickerOption) => (
                            <ComboboxItem key={option.value} value={option} title={option.label}>
                                {option.label}
                            </ComboboxItem>
                        )}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>

            {selectedItems.length > 0 && (
                <div className="mt-4">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('selected-items')}</TableHead>
                                <TableHead className="w-15">{t('actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {selectedItems.map((item) => (
                                <TableRow key={item.value}>
                                    <TableCell>{item.label}</TableCell>
                                    <TableCell>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-sm"
                                            className="text-destructive"
                                            aria-label={t('delete')}
                                            onClick={() => handleDelete(item.value)}
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default ListPickerInput;
